import { Logger } from '~~/helpers/logger';

import { EXPONDO_MIDDLEWARE_ROUTES } from '~~/constants/routes';
import type { DiscountCode, DiscountCodeQueryResult } from '~~/types/commercetools';
import isDateTodayOrPast from '~~/helpers/time/isDateTodayOrPast';
import isDateTodayOrFuture from '~~/helpers/time/isDateTodayOrFuture';

export const useVoucherValidation = (voucherCode: string, voucherValidUntil?: string) => {

  const { makeCommercetoolsRequest } = useExpondoMiddleware();

  const loading = ref(true);
  const voucherExists = useState(`useVoucherValidation-voucherExists-${voucherCode}`, () => false);
  const vouchers: Ref<DiscountCode[]> = useState(`useVoucherValidation-vouchers-${voucherCode}`, () => []);

  const search = async () => {

    if (!voucherCode) {

      loading.value = false;
      voucherExists.value = false;
      vouchers.value = [];

      return;
    
    }

    try {

      loading.value = true;
      const response = await makeCommercetoolsRequest<{
        discountCodes: DiscountCodeQueryResult;
      }>({
        endpoint: EXPONDO_MIDDLEWARE_ROUTES.CT.GET_DISCOUNT_CODE,
        data: [{ voucherCode }],
      });

      voucherExists.value = response?.data?.discountCodes?.exists || false;
      vouchers.value = response?.data?.discountCodes?.results || [];
    
    } catch (err) {

      Logger.error('error.useVoucherValidation', String(err));
    
    } finally {

      loading.value = false;
    
    }
  
  };

  const voucherIsActive = computed<boolean>(() => {

    const voucher = vouchers.value[0];

    return !!voucher?.isActive;
  
  });

  const maxApplicationsReached = computed<boolean>(() => {

    const maxApplications = vouchers.value[0]?.maxApplications;
    const applicationCount = vouchers.value[0]?.applicationCount;

    return !maxApplications ? false : applicationCount >= maxApplications;
  
  });

  const voucherHasStarted = computed<boolean>(() => {

    const validFrom = vouchers.value[0]?.validFrom;

    return !validFrom ? true : isDateTodayOrPast(validFrom);
  
  });

  const voucherHasNotExpired = computed<boolean>(() => {

    if (voucherValidUntil) {

      return isDateTodayOrFuture(voucherValidUntil);
    
    }

    const validUntil = vouchers.value[0]?.validUntil;

    return !validUntil ? true : isDateTodayOrFuture(validUntil);
  
  });

  const voucherIsWithinDateRange = computed<boolean>(() => {

    return voucherHasStarted.value && voucherHasNotExpired.value;
  
  });

  const voucherIsValid = computed<boolean>(() => {

    return voucherIsActive.value && !maxApplicationsReached.value && voucherIsWithinDateRange.value;
  
  });

  return {
    search,
    loading,
    voucherExists: computed(() => voucherExists.value),
    voucherIsValid,
  };

};
